import { getQueryKey as getIdeaQueryKey } from 'common/actions/ideas';
import { DefaultFieldNames, IdeaFieldTypes } from 'common/constants/ideas';
import {
  BooleanConditions,
  DropdownConditions,
  FiltersOperator,
  MultiselectConditions,
  NumericConditions,
} from 'common/fieldFilters/utils';
import { NoOwnerID } from 'common/subdomain/admin/v2/AdminV2IdeasTableV2/constants';
import {
  type QueryParams,
  getQueryKey,
} from 'common/subdomain/admin/v2/containers/IdeaListContainer';
import { isNotNil } from 'common/util/isNil';
import { compareSortValues } from 'common/util/sortIdeas';
import { isFilterDefaultField, isFilterIdeaCustomField } from 'common/views/filters';

import type { Header, Row, Table } from '@tanstack/react-table';
import type { Group } from 'common/api/endpoints/group';
import type { Idea, IdeaField, UpdateIdeaFields } from 'common/api/endpoints/idea';
import type { View } from 'common/api/endpoints/view';
import type { IdeaIDQueriesState, IdeaQueryState } from 'common/reducers/ideaQueries';
import type { IdeasState } from 'common/reducers/ideas';
import type {
  GroupTableItem,
  IdeaTableItem,
  Section,
  SectionTableItem,
  TableItem,
  TableItemType,
} from 'common/subdomain/admin/v2/AdminV2IdeasTableV2/types';

export const bypassedIdeaMap: Record<string, Idea> = {};

export const addBypassedIdea = (idea: Idea) => {
  if (!bypassedIdeaMap[idea._id]) {
    bypassedIdeaMap[idea._id] = idea;
  }
};

export const clearBypassedIdeaMap = () => {
  Object.keys(bypassedIdeaMap).forEach((ideaID) => {
    delete bypassedIdeaMap[ideaID];
  });
};

type BuildIdeasTreeOptions = {
  ideas: Idea[];
  groups?: Group[];
  groupBy?: View['groupBy'];
  selectedGroupId?: string;
  lastItem: Idea | null | undefined;
  sort?: View['sort'];
};

/**
 * Builds a tree of ideas and groups
 */
export function buildIdeasTree({
  ideas,
  groups,
  groupBy,
  selectedGroupId,
  lastItem,
  sort,
}: BuildIdeasTreeOptions): TableItem[] {
  if (groupBy === 'hierarchy' && groups) {
    return buildHierarchyIdeasTree(ideas, groups, selectedGroupId, lastItem);
  } else if (groupBy === 'owner' || groupBy === 'status') {
    return buildSectionedIdeasTree(ideas, groupBy, sort);
  } else {
    return buildFlatIdeasTree(ideas);
  }
}

/**
 * Builds a flat tree of ideas
 */
function buildFlatIdeasTree(ideas: Idea[]): TableItem[] {
  const itemMap = createMap(
    ideas.map((idea) => createItem(idea, 'idea') as IdeaTableItem),
    '_id',
  );

  const rootItems: TableItem[] = [];

  ideas.forEach((idea) => {
    const ideaItem = itemMap.get(idea._id)!;
    addItemToParentOrRoot(ideaItem, idea.parentID, itemMap, rootItems);
  });

  return rootItems;
}

/**
 * Builds a hierarchy of ideas + groups
 */
function buildHierarchyIdeasTree(
  ideas: Idea[],
  groups: Group[],
  selectedGroupId?: string,
  lastItem?: Idea | null,
): TableItem[] {
  const ideaMap = createMap(ideas, '_id');
  const groupMap = createMap(groups, '_id');
  const itemMap = new Map<string, TableItem>();

  ideas.forEach((idea) => {
    itemMap.set(idea._id, createItem(idea, 'idea'));
  });

  groups.forEach((group) => {
    itemMap.set(group._id, createItem(group, 'group'));
  });

  const rootItems: TableItem[] = [];

  processIdeas(ideas, groupMap, ideaMap, itemMap, rootItems, selectedGroupId);
  processGroups(groups, groupMap, ideaMap, itemMap, rootItems, selectedGroupId, lastItem);

  calculateChildCounts(rootItems);
  return rootItems;
}

/**
 * Builds a sectioned tree of ideas (e.g. group by owner/status)
 */
function getSectionID(idea: Idea, groupBy: 'owner' | 'status') {
  return idea.fields[groupBy];
}

function buildSectionedIdeasTree(ideas: Idea[], groupBy: 'owner' | 'status', sort?: View['sort']) {
  const sectionItems: Record<string, Idea[]> = {};
  const sectionSortKeys: Record<string, string | number | Date | null> = {};

  ideas.forEach((idea) => {
    const _idea = bypassedIdeaMap[idea._id] ? bypassedIdeaMap[idea._id] : idea;
    const currentSectionID = getSectionID(idea, groupBy) ?? NoOwnerID;

    // Check if idea is bypassed, if so use the stored original section ID to avoid
    // moving the idea around
    const sectionID = _idea.fields[groupBy] ?? currentSectionID;

    sectionItems[sectionID] = sectionItems[sectionID] || [];
    sectionItems[sectionID].push(idea);

    // Store the sort key for this section from the first idea
    if (sectionSortKeys[sectionID] === undefined) {
      const sortKey = _idea.sortKeys[groupBy];
      sectionSortKeys[sectionID] = sortKey !== undefined ? sortKey : null;
    }
  });

  // Determine sort direction: if sorting by the same field we're grouping by, respect the direction
  const isSortingBySameField = sort?.field === groupBy;
  const sortDirection = isSortingBySameField && sort?.direction === 'desc' ? -1 : 1;

  // Sort section IDs by their sort keys to maintain a stable order
  const sectionIDs = Object.keys(sectionItems).sort((a, b) => {
    return compareSortValues(sectionSortKeys[a], sectionSortKeys[b]) * sortDirection;
  });

  const rootItems: TableItem[] = [];

  sectionIDs.forEach((sectionID) => {
    const section = { _id: sectionID, sectionType: groupBy };
    const sectionItem = createItem(section, 'section');
    rootItems.push(sectionItem);

    sectionItems[sectionID].forEach((idea) => {
      const ideaItem = createItem(idea, 'idea');
      sectionItem.subRows!.push(ideaItem);
    });
  });

  calculateChildCounts(rootItems);
  return rootItems;
}

/**
 * Builds a complete ideas tree with loaded sub-ideas recursively
 */
export const buildCompleteIdeasTree = (
  initialData: TableItem[],
  getParentSubIdeasQuery: (parentIdeaId: string) => IdeaQueryState,
  lastItem: Idea | null | undefined,
): TableItem[] => {
  const buildCompleteTree = (items: TableItem[]): TableItem[] => {
    return items.map((item) => {
      if (isIdea(item) && item.childCount > 0) {
        const loadedSubIdeas = getParentSubIdeasQuery(item._id)?.items ?? [];

        if (loadedSubIdeas.length > 0) {
          const subTree = buildIdeasTree({
            ideas: loadedSubIdeas,
            lastItem,
          });
          return {
            ...item,
            subRows: buildCompleteTree(subTree),
            childCount: item.childCount,
          };
        }
      }

      if (item.subRows && item.subRows?.length > 0) {
        return {
          ...item,
          subRows: buildCompleteTree(item.subRows),
        };
      }

      return item;
    });
  };

  return buildCompleteTree(initialData);
};

export const getSectionIDs = (data: TableItem[]): string[] => {
  return data.filter(isSection).map((section) => section._id);
};

export const getGroupRowIds = (data: TableItem[]): string[] => {
  const groupIds: string[] = [];

  data.forEach((item) => {
    if (isGroup(item)) {
      groupIds.push(item._id);
    }

    if (item.subRows && item.subRows?.length > 0) {
      groupIds.push(...getGroupRowIds(item.subRows));
    }
  });

  return groupIds;
};

export const isGroup = (item: TableItem): item is GroupTableItem => {
  return item.type === 'group';
};

export const isIdea = (item: TableItem): item is IdeaTableItem => {
  return item.type === 'idea';
};

export const isSection = (item: TableItem): item is SectionTableItem => {
  return item.type === 'section';
};

export const getIdeaQuery = ({
  ideas,
  ideaQueries,
  queryParams,
}: {
  ideas: IdeasState;
  ideaQueries: IdeaIDQueriesState;
  queryParams: QueryParams;
}): IdeaQueryState => {
  const ideaQueryIDOnly = ideaQueries[getQueryKey(queryParams)];
  const ideaQueryItems = ideaQueryIDOnly?.items
    ?.map((ideaIDOnly) => {
      return ideas[getIdeaQueryKey({ ideaID: ideaIDOnly._id })]?.items?.[0];
    })
    .filter(isNotNil);

  return {
    ...ideaQueryIDOnly,
    items: ideaQueryItems,
  };
};

/**
 * Calculates the depth of ideas only, excluding groups
 * This is used to determine if we can add sub-ideas based on MaxIdeaDepth
 */
export const getIdeaOnlyDepth = (row: Row<TableItem>, groupIds: Set<string>): number => {
  if (isGroup(row.original)) {
    return row.depth - 1;
  }

  if (isIdea(row.original)) {
    const idea = row.original as Idea;
    const ideaAncestors = idea.ancestorIDs?.filter((id) => !groupIds.has(id)) || [];
    return ideaAncestors.length;
  }

  return row.depth;
};

/**
 * Creates a Map from an array using the specified key
 */
function createMap<T, K extends keyof T>(items: T[], key: K): Map<T[K], T> {
  return new Map(items.map((item) => [item[key], item]));
}

/**
 * Creates a TableItem from an Idea or Group
 */
function createItem(item: Idea | Group | Section, type: TableItemType): TableItem {
  const isIdea = type === 'idea';
  return {
    ...item,
    subRows: [],
    type,
    childCount: isIdea ? (item as Idea).childCount : 0, // Use existing childCount for ideas, 0 for groups
  } as TableItem;
}

/**
 * Processes groups and adds them to the tree structure
 */
function processGroups(
  groups: Group[],
  groupMap: Map<string, Group>,
  ideaMap: Map<string, Idea>,
  itemMap: Map<string, TableItem>,
  rootItems: TableItem[],
  selectedGroupId: string | undefined,
  lastItem: Idea | null | undefined,
): void {
  groups.forEach((group) => {
    if (!shouldIncludeItem(group._id, 'group', groupMap, ideaMap, selectedGroupId)) {
      return;
    }

    const lastItemGroup = lastItem?.groupID ? groupMap.get(lastItem.groupID) : null;
    if (lastItemGroup && group.sortKey > lastItemGroup.sortKey) {
      return;
    }

    const groupItem = itemMap.get(group._id)!;
    addItemToParentOrRoot(groupItem, group.parentID, itemMap, rootItems, (parentId) =>
      shouldIncludeItem(parentId, 'group', groupMap, ideaMap, selectedGroupId),
    );
  });
}

/**
 * Processes ideas and adds them to the tree structure
 */
function processIdeas(
  ideas: Idea[],
  groupMap: Map<string, Group>,
  ideaMap: Map<string, Idea>,
  itemMap: Map<string, TableItem>,
  rootItems: TableItem[],
  selectedGroupId: string | undefined,
): void {
  ideas.forEach((idea) => {
    if (!shouldIncludeItem(idea._id, 'idea', groupMap, ideaMap, selectedGroupId)) {
      return;
    }

    const ideaItem = itemMap.get(idea._id)!;
    const parentId = determineIdeaParent(idea, groupMap);

    addItemToParentOrRoot(ideaItem, parentId, itemMap, rootItems, (parentId) =>
      shouldIncludeItem(
        parentId,
        getItemType(parentId, itemMap),
        groupMap,
        ideaMap,
        selectedGroupId,
      ),
    );
  });
}

/**
 * Determines the parent ID for an idea based on group and parent relationships
 */
function determineIdeaParent(idea: Idea, groupMap: Map<string, Group>): string | null {
  const _idea = bypassedIdeaMap[idea._id] ? bypassedIdeaMap[idea._id] : idea;

  if (_idea.groupID) {
    const group = groupMap.get(_idea.groupID);
    if (group?.parentID === _idea._id) {
      return null;
    }
    if (shouldPlaceInGroup(_idea, _idea.groupID, groupMap)) {
      return _idea.groupID;
    }
    if (idea.parentID) {
      return idea.parentID;
    }
  } else if (idea.parentID) {
    return idea.parentID;
  }
  return null;
}

/**
 * Gets the type of an item from the itemMap
 */
function getItemType(itemId: string, itemMap: Map<string, TableItem>): 'group' | 'idea' {
  return itemMap.get(itemId)?.type as 'group' | 'idea';
}

/**
 * Adds an item to its parent or to root items
 */
function addItemToParentOrRoot(
  item: TableItem,
  parentId: string | null | undefined,
  itemMap: Map<string, TableItem>,
  rootItems: TableItem[],
  shouldIncludeParent?: (parentId: string) => boolean,
): void {
  if (parentId) {
    const parentItem = itemMap.get(parentId);
    if (parentItem && (!shouldIncludeParent || shouldIncludeParent(parentId))) {
      parentItem.subRows = parentItem.subRows || [];
      parentItem.subRows.push(item);
      return;
    }
  }
  rootItems.push(item);
}

/**
 * Determines if an idea should be placed in a specific group
 */
function shouldPlaceInGroup(idea: Idea, groupId: string, groupMap: Map<string, Group>): boolean {
  if (!idea.groupID || idea.groupID !== groupId) {
    return false;
  }

  const group = groupMap.get(groupId);
  if (!group) {
    return false;
  }

  if (!idea.parentID) {
    return true;
  }

  return group.ancestorIDs.includes(idea.parentID) || group.parentID === idea.parentID;
}

/**
 * Calculates child counts for all groups recursively
 */
function calculateChildCounts(items: TableItem[]): void {
  items.forEach((item) => {
    if (item.subRows && item.subRows?.length > 0) {
      if (isGroup(item)) {
        item.childCount = item.subRows.filter((subItem) => isIdea(subItem)).length;
      } else if (isSection(item)) {
        item.childCount = item.subRows.length;
      }
      calculateChildCounts(item.subRows);
    } else {
      if (isGroup(item)) {
        item.childCount = 0;
      } else if (isSection(item)) {
        item.childCount = 0;
      }
    }
  });
}

/**
 * Determines if an item should be included based selected group
 */
function shouldIncludeItem(
  itemId: string,
  itemType: 'group' | 'idea',
  groupMap: Map<string, Group>,
  ideaMap: Map<string, Idea>,
  selectedGroupId: string | undefined,
): boolean {
  if (itemType === 'idea' && bypassedIdeaMap[itemId]) {
    return true;
  }

  if (!selectedGroupId) {
    return true;
  }

  if (itemType === 'group') {
    const group = groupMap.get(itemId);
    if (!group) {
      return false;
    }
    // When a group is selected, exclude the root group and only include descendants
    return group._id !== selectedGroupId && isDescendantOfGroup(group, selectedGroupId, groupMap);
  }

  const idea = ideaMap.get(itemId);
  if (!idea) {
    return false;
  }

  // Include ideas that belong directly to the selected group
  if (idea.groupID === selectedGroupId) {
    return true;
  }

  // Include ideas that belong to descendant groups of the selected group
  if (idea.groupID) {
    const group = groupMap.get(idea.groupID);
    if (group && isDescendantOfGroup(group, selectedGroupId, groupMap)) {
      return true;
    }
  }

  // Also include ideas that are direct children of descendant groups
  if (idea.parentID) {
    const parentGroup = groupMap.get(idea.parentID);
    if (parentGroup && isDescendantOfGroup(parentGroup, selectedGroupId, groupMap)) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if a group is a descendant of another group
 */
function isDescendantOfGroup(
  group: Group,
  ancestorId: string,
  groupMap: Map<string, Group>,
): boolean {
  let currentGroup = group;
  while (currentGroup.parentID) {
    if (currentGroup.parentID === ancestorId) {
      return true;
    }
    const parent = groupMap.get(currentGroup.parentID);
    if (!parent) {
      break;
    }
    currentGroup = parent;
  }
  return false;
}

export const getRowDepthStyle = (depth: number): React.CSSProperties => {
  return { '--row-depth': depth } as React.CSSProperties;
};

export const isTitleColumn = (id: string) => {
  return id === DefaultFieldNames.Title;
};

export const getColumnPinningInfo = (table: Table<TableItem>, fieldName: string) => {
  const column = table.getColumn(fieldName);
  const isPinned = column?.getIsPinned();
  const columnOffset =
    isPinned === 'left' ? column?.getStart(isPinned) : column?.getAfter(isPinned);
  const isLastPinnedColumn =
    isPinned === 'left' ? column?.getIsLastColumn(isPinned) : column?.getIsFirstColumn(isPinned);

  return { isPinned, columnOffset, isLastPinnedColumn };
};

export const getHeaderPinningInfo = (header: Header<TableItem, unknown>) => {
  const isPinned = header.column.getIsPinned();
  const columnOffset =
    isPinned === 'left' ? header.column.getStart(isPinned) : header.column.getAfter(isPinned);
  const isLastPinnedColumn =
    isPinned === 'left'
      ? header.column.getIsLastColumn(isPinned)
      : header.column.getIsFirstColumn(isPinned);

  return { isPinned, columnOffset, isLastPinnedColumn };
};

/* Inline Create Helpers */

const isSupportedFieldType = (fieldType: IdeaFieldTypes): boolean => {
  // These are not supported
  return (
    fieldType !== IdeaFieldTypes.date &&
    fieldType !== IdeaFieldTypes.url &&
    fieldType !== IdeaFieldTypes.text &&
    fieldType !== IdeaFieldTypes.multilineText
  );
};

/**
 * Extracts the value from a filter based on the condition.
 * Returns undefined if the condition is not supported.
 */
const getValueFromFilter = (
  condition: string,
  filterValue: string | string[] | null,
  fieldType: IdeaFieldTypes,
  fieldID: string,
): string | string[] | number | boolean | null | undefined => {
  if (fieldType === IdeaFieldTypes.number) {
    if (condition === NumericConditions.equals) {
      return typeof filterValue === 'string' ? Number(filterValue) : null;
    }
    // doesNotEqual, greaterThan, lessThan are not supported
    return undefined;
  }

  if (fieldType === IdeaFieldTypes.boolean) {
    if (condition === BooleanConditions.isTrue) {
      return true;
    }
    if (condition === BooleanConditions.isFalse) {
      return false;
    }
    return undefined;
  }

  if (fieldType === IdeaFieldTypes.dropdown || fieldType === IdeaFieldTypes.user) {
    if (condition === DropdownConditions.is) {
      return typeof filterValue === 'string' ? filterValue : null;
    }
    if (condition === DropdownConditions.isDropdownNotSet) {
      return null;
    }
    // isNot, isDropdownSet are not supported
    return undefined;
  }

  if (fieldType === IdeaFieldTypes.multiselect) {
    if (condition === MultiselectConditions.isOneOf) {
      // Take the first option
      if (Array.isArray(filterValue) && filterValue.length > 0) {
        return fieldID === DefaultFieldNames.Status ? filterValue[0] : [filterValue[0]];
      }
      return undefined;
    }
    if (condition === MultiselectConditions.isMultiselectNotSet) {
      return null;
    }
    // isNotOneOf, isMultiselectSet are not supported
    return undefined;
  }

  return undefined;
};

const applySectionData = (section: Section, fieldValues: UpdateIdeaFields) => {
  if (section.sectionType === 'owner' && section._id !== NoOwnerID) {
    fieldValues[DefaultFieldNames.Owner] = section._id;
  } else if (section.sectionType === 'status') {
    fieldValues[DefaultFieldNames.Status] = section._id;
  }
};

export const getIdeaCreateData = ({
  selectedView,
  row,
  fieldsConfig,
}: {
  selectedView: View;
  row: Row<TableItem> | null;
  fieldsConfig: IdeaField[];
}): { fieldValues: UpdateIdeaFields; groupID: string | null; parentID: string | null } => {
  const fieldTypeMap = new Map<string, IdeaFieldTypes>();
  fieldsConfig.forEach((field) => fieldTypeMap.set(field.name, field.type));

  let groupID: string | null = selectedView.groupID;
  let parentID: string | null = null;
  const fieldValues: UpdateIdeaFields = {};

  if (row) {
    const { original } = row;

    if (isGroup(original)) {
      groupID = original._id;
    } else if (isSection(original)) {
      applySectionData(original, fieldValues);
    } else if (isIdea(original)) {
      parentID = original._id;
      groupID = original.groupID;

      const parentSection = row.getParentRows().find((r) => isSection(r.original));
      if (parentSection && isSection(parentSection.original)) {
        applySectionData(parentSection.original, fieldValues);
      }
    }
  }

  const uniqueFilterFields = new Set(
    selectedView.filters
      .filter((f) => isFilterIdeaCustomField(f) || isFilterDefaultField(f))
      .map((f) => f.value.fieldID),
  );

  // We apply filters only if:
  // - The filters operator is all
  // - The filters operator is any and there is only one filter or all filters are on the same field
  const shouldApplyFilters =
    selectedView.filtersOperator === FiltersOperator.all ||
    (selectedView.filtersOperator === FiltersOperator.any &&
      (selectedView.filters.length === 1 || uniqueFilterFields.size === 1));

  if (!shouldApplyFilters) {
    return { fieldValues, groupID, parentID };
  }

  selectedView.filters.forEach((filter) => {
    if (!isFilterIdeaCustomField(filter) && !isFilterDefaultField(filter)) {
      return;
    }

    const { condition, value } = filter;
    const fieldID = value.fieldID;
    const fieldType = fieldTypeMap.get(fieldID);

    if (!fieldType) {
      return;
    }

    if (!isSupportedFieldType(fieldType)) {
      return;
    }

    const extractedValue = getValueFromFilter(condition, value.value, fieldType, fieldID);

    // If we have more than one filter for the same field, we only want to set the value once
    if (extractedValue !== undefined && fieldValues[fieldID] === undefined) {
      fieldValues[fieldID] = extractedValue;
    }
  });

  return { fieldValues, groupID, parentID };
};

export function getRowRange<T>(rows: Array<Row<T>>, idA: string, idB: string) {
  const range: Array<Row<T>> = [];
  let foundStart = false;
  let foundEnd = false;
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (row.id === idA || row.id === idB) {
      if (foundStart) {
        foundEnd = true;
      }
      if (!foundStart) {
        foundStart = true;
      }
    }

    if (foundStart) {
      range.push(row);
    }

    if (foundEnd) {
      break;
    }
  }

  return range;
}
